// PRC-030: campo de contraseña común: botón del ojo, aviso «Mayúsculas
// activadas» y requisitos visibles mientras se escribe (boceto A1).
import React, { useState } from 'react';
import { Box, IconButton, InputBaseProps } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { J } from '../journeyTokens';
import { MIN_PASSWORD_LENGTH } from '../journeyAuth';
import { JourneyField } from './JourneyBlocks';

type PasswordFieldProps = Omit<InputBaseProps, 'error' | 'type'> & {
  id: string;
  label: string;
  error?: React.ReactNode;
  help?: React.ReactNode;
  // current-password (entrar) o new-password (crear o cambiar).
  autoComplete: 'current-password' | 'new-password';
  // Muestra los requisitos de una contraseña nueva mientras se escribe.
  showRequirements?: boolean;
};

export const passwordMeetsRequirements = (value: string) => value.length >= MIN_PASSWORD_LENGTH;

const PasswordField: React.FC<PasswordFieldProps> = ({ id, label, error, help, autoComplete, showRequirements, value, onKeyDown, onKeyUp, onBlur, ...input }) => {
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const text = typeof value === 'string' ? value : '';
  const checkCaps = (event: React.KeyboardEvent) => {
    if (typeof event.getModifierState === 'function') setCapsLock(event.getModifierState('CapsLock'));
  };
  const met = passwordMeetsRequirements(text);
  const notes = (
    <>
      {error}
      {!error && showRequirements && (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: met ? J.okText : J.muted, fontWeight: met ? 600 : 400 }}>
          <Box component="span" aria-hidden sx={{ width: 16, height: 16, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${met ? J.ok : J.borderStrong}`, bgcolor: met ? 'rgba(74,222,128,.12)' : 'transparent', '& svg': { fontSize: 12 } }}>
            {met && <CheckIcon />}
          </Box>
          Mínimo {MIN_PASSWORD_LENGTH} caracteres{met ? ' · cumplido' : ''}
        </Box>
      )}
      {!error && !showRequirements && help}
      {capsLock && (
        <Box component="span" role="status" sx={{ display: 'block', color: J.amber, mt: error || showRequirements || help ? '4px' : 0 }}>
          Mayúsculas activadas
        </Box>
      )}
    </>
  );
  const hasNotes = !!error || !!showRequirements || !!help || capsLock;
  return (
    <JourneyField
      id={id}
      label={label}
      type={visible ? 'text' : 'password'}
      autoComplete={autoComplete}
      value={value}
      error={error ? notes : undefined}
      help={!error && hasNotes ? notes : undefined}
      onKeyDown={(event) => { checkCaps(event); onKeyDown?.(event); }}
      onKeyUp={(event) => { checkCaps(event); onKeyUp?.(event); }}
      onBlur={(event) => { setCapsLock(false); onBlur?.(event); }}
      endAdornment={(
        <IconButton
          size="small"
          edge="end"
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          onClick={() => setVisible((v) => !v)}
          onMouseDown={(event) => event.preventDefault()}
          sx={{ color: J.muted, mr: '-6px', '&:hover': { color: J.text } }}
        >
          {visible ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
        </IconButton>
      )}
      {...input}
    />
  );
};

export default PasswordField;
